/**
 * Custom hook for handling cart initialization logic
 */

import { useEffect } from "react";
import { CartData } from "../../interfaces/CartData";
import * as intrfce from "./Interfaces";
import nvCookieHelpers from "../../services/cookieService";

interface UseCartInitializationProps {
  orderState: string | null;
  cartData: CartData | undefined;
  stateDropdown: boolean;
  locale: string;
  countyField: boolean;
  formBillingAddress: intrfce.iAddressFormData;
  t: (key: string) => string;
  fetchCart: () => Promise<void>;
  emptyCart: (msg: string) => void;
  hideBodyLoader: () => void;
  showBodyLoader: () => void;
  handleSavedBillingAddressChange: (fieldName: string, value: string) => void;
  setFormBillingData: (data: intrfce.iBillingFormData | ((prev: intrfce.iBillingFormData) => intrfce.iBillingFormData)) => void;
  setFormBillingAddress: (address: intrfce.iAddressFormData | ((prev: intrfce.iAddressFormData) => intrfce.iAddressFormData)) => void;
  setFormShippingAddress: (address: intrfce.iAddressFormData | ((prev: intrfce.iAddressFormData) => intrfce.iAddressFormData)) => void;
  setSelectedBillingAddress: (address: intrfce.iSavedAddress | null) => void;
  setFormData: (data: intrfce.iFormContainer | ((prev: intrfce.iFormContainer) => intrfce.iFormContainer)) => void;
  setSelectedShippingAddress: (address: intrfce.iSavedAddress | null) => void;
  setFormTaxData: (data: intrfce.iTaxFormData) => void;
}

/**
 * Handles cart initialization on component mount
 */
export const useCartInitialization = (props: UseCartInitializationProps) => {
  const {
    orderState,
    cartData,
    stateDropdown,
    locale,
    countyField,
    t,
    fetchCart,
    emptyCart,
    hideBodyLoader,
    showBodyLoader,
    handleSavedBillingAddressChange,
    setFormBillingData,
    setFormBillingAddress,
    setFormShippingAddress,
    setSelectedBillingAddress,
    setFormData,
    setSelectedShippingAddress,
    setFormTaxData,
  } = props;

  useEffect(() => {
    const sessionexpiredID = nvCookieHelpers.getCookie("session_expired");
    const isSessionValid = sessionexpiredID && !JSON.parse(sessionexpiredID);

    if (orderState) {
      if (isSessionValid) {
        fetchCart();
      } else {
        emptyCart(t("YOUR_SHOPPING_CART_IS_CURRENTLY_EMPTY"));
      }
      hideBodyLoader();
      return;
    }

    if (!cartData) {
      if (isSessionValid) {
        fetchCart();
      } else {
        emptyCart(t("YOUR_SHOPPING_CART_IS_CURRENTLY_EMPTY"));
      }
      return;
    }

    // Initialize cart data
    const parsedFormData = cartData;
    showBodyLoader();

    // Handle billing address
    if (parsedFormData?.billingAddress) {
      const billing = parsedFormData.billingAddress;
      
      if (Number(billing.id)) {
        handleSavedBillingAddressChange("savedBillingAddress", "reset");
        
        setFormBillingData({
          ...(billing.id ? { id: Number(billing.id) } : {}),
          firstNameBilling: billing.firstName || "",
          lastNameBilling: billing.lastName || "",
          contactBilling: billing.phoneNumber || "",
          emailBilling: billing.emailAddress || "",
          vemailBilling: billing.emailAddress || ""
        });

        setFormBillingAddress({
          pincode: billing.postalCode || "",
          address: billing.line1 || "",
          line2: billing.line2 || "",
          cityValue: billing.city || "",
          stateValue:
            (stateDropdown || locale === 'zh-tw')
              ? (locale === 'zh-tw' ? "TW" : billing.countrySubdivision)
              : '',
          county: countyField ? billing.county || "" : '',
          countryName: billing.countryName,
          countryValue: billing.country,
        });

        setSelectedBillingAddress({
          city: billing.city || "",
          countryName: billing.countryName,
          country: billing.country,
          firstName: billing.firstName || "",
          lastName: billing.lastName || "",
          line1: billing.line1 || "",
          line2: billing.line2,
          nickName: "",
          phoneNumber: billing.phoneNumber || "",
          postalCode: billing.postalCode || "",
          countrySubdivision: billing.countrySubdivision || "",
          ...(countyField && { county: billing.county }),
        });
      }
    }

    // Handle shipping address
    if (parsedFormData.shippingAddress && parsedFormData.billingAddress) {
      const billing = parsedFormData.billingAddress;
      const shipping = parsedFormData.shippingAddress;

      setFormData({
        ...(shipping.id ? { id: Number(shipping.id) } : {}),
        firstNameShiping: shipping?.firstName || "",
        lastNameShiping: shipping?.lastName || "",
        contactShiping: shipping?.phoneNumber || "",
        iBillingFormData: {
          ...(billing.id ? { id: Number(billing.id) } : {}),
          firstNameBilling: billing?.firstName,
          lastNameBilling: billing?.lastName,
          contactBilling: billing?.phoneNumber,
          emailBilling: billing?.emailAddress,
          vemailBilling: billing?.emailAddress
        },
        iShippingDetails: {
          shippingOption: parsedFormData.shippingOptions.shippingOption
        },
        iBillingFormAddress: {
          pincode: billing?.postalCode || "",
          address: billing?.line1 || "",
          line2: billing?.line2 || "",
          cityValue: billing?.city || "",
          stateValue:
            (stateDropdown || locale === 'zh-tw')
              ? (locale === 'zh-tw' ? "TW" : billing?.countrySubdivision)
              : '',
          county: countyField ? billing?.county || "" : '',
          countryValue: billing?.country || "",
          countryName: billing?.countryName || "",
        },
        iShippingFormAddress: {
          pincode: shipping?.postalCode || "",
          address: shipping?.line1 || "",
          line2: shipping?.line2 || "",
          cityValue: shipping?.city || "",
          stateValue:
            (stateDropdown || locale === 'zh-tw')
              ? (locale === 'zh-tw' ? "TW" : shipping?.countrySubdivision)
              : '',
          county: countyField ? shipping?.county || "" : '',
          countryValue: shipping?.country || "",
          countryName: shipping?.countryName || "",
        },
      });

      setFormShippingAddress({
        pincode: shipping?.postalCode || "",
        address: shipping?.line1 || "",
        line2: shipping?.line2 || "",
        cityValue: shipping?.city || "",
        stateValue:
          (stateDropdown || locale === 'zh-tw')
            ? (locale === 'zh-tw' ? "TW" : shipping?.countrySubdivision)
            : '',
        county: countyField ? shipping?.county || "" : '',
        countryValue: shipping?.country || "",
        countryName: shipping?.countryName || "",
      });

      setSelectedShippingAddress({
        ...(shipping.id ? { id: Number(shipping.id) } : {}),
        city: shipping.city || "",
        countryName: shipping.countryName,
        country: shipping.country,
        firstName: shipping.firstName || "",
        isDefault: true,
        lastName: shipping.lastName || "",
        line1: shipping.line1 || "",
        line2: shipping.line2 || "",
        nickName: "",
        phoneNumber: shipping.phoneNumber || "",
        postalCode: shipping.postalCode || "",
        countrySubdivision:
          (stateDropdown || locale === 'zh-tw')
            ? (locale === 'zh-tw' ? "TW" : shipping.countrySubdivision)
            : '',
        county: countyField ? shipping.county || "" : '',
      });

      setFormTaxData({
        companyName: "",
        vatNo: "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

